import { Component, ErrorInfo, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography, useTheme
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { Chart, ChartConfiguration, registerables } from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { DateTime } from 'luxon'
import { sprintf } from 'sprintf-js'
import { BoilerIcon, RadiatorIcon } from '../svg/icons'
import { Port } from '../types/models'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { RadioSelector } from './atoms/RadioSelector'
import { PageTitle } from './atoms/PageTitle'
import { ChartSummary } from './charts/ChartSummary'
import { midpointLinePlugin } from './charts/midpointLinePlugin'
import { quarterHourBandsPlugin, quarterHourLabels } from './charts/quarterHourBands'
import { SeriesGroup } from '../utils/series'
import { lang } from '../utils/lang'
import { reportClientError } from '../utils/error-handler'
import {
  HEAT_PUMP_INCOMPLETE, HeatPumpHistoryRow, HeatPumpScale, heatPumpChartValue, heatPumpElectricityPrice, heatPumpHeatPrice, heatPumpOnSlots,
  heatPumpIsolated, heatPumpLatestSlice, heatPumpNoElectricity, heatPumpPageAfter, heatPumpPageBefore, heatPumpRows, heatPumpShowsPower,
  heatPumpSlots, heatPumpSplit, heatPumpSwitchAnchor, heatPumpTotals, heatPumpWindowAround, heatPumpWithFees
} from '../utils/heat-pump-history'

Chart.register(zoomPlugin, ...registerables)

/** Load the page before / after the loaded range when the view comes this close (in slices) to its edge. */
const LOAD_MORE_THRESHOLD = 3
/** Stop loading older pages after this many empty ones in a row (before the heat pump's first data). */
const MAX_EMPTY_PAGES = 2

/** Hot water. Space heating uses the secondary (magenta) colour of the theme. */
const DHW_COLOR = '#2BA3C4'
/** Heat produced: a neutral envelope behind the electricity bars, darker for space heating, lighter for hot water on top. */
const HEAT_SPACE_COLOR = '#3E454D'
const HEAT_DHW_COLOR = '#646C76'
/** Label colour of the heat series in the chart header: lighter than the bars, readable on the page background. */
const HEAT_LABEL_COLOR = '#9FA1AB'
/** Grid and ticks as in the meter, battery and price history charts. */
const GRID = { color: '#dfdfdf', lineWidth: 0.075 }
const AXIS_WIDTH = 64

export interface HeatPumpHistoryProps {
  port: Port
  /** Whether the money figures include margin and transfer fee (the fee switch of the History page). */
  includeFees: boolean
}

function num(value: number, digits: number): string {
  if (!Number.isFinite(value)) return '–'
  return sprintf(`%.${digits}f`, value).replace('.', ',')
}

function kwh(value: number): string {
  return `${num(value, value >= 100 ? 0 : 1)} kWh`
}

function eur(value: number): string {
  return `${num(value, 2)} €`
}

function cop(value: number | null): string {
  return value === null ? '–' : num(value, 1)
}

/**
 * A rounded top for a y axis from zero, with about four steps: 103 kWh -> 120 (steps of 30), 3,6 € -> 4 (steps of 1).
 * Chart.js alone rounds 103 up to 150, leaving a third of the chart empty.
 */
export function heatPumpAxis(max: number): { max: number, stepSize: number } {
  if (!(max > 0)) return { max: 1, stepSize: 0.2 }
  const raw = max / 4
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 2.5, 3, 4, 5, 6, 8, 10].map(f => f * magnitude).find(v => v >= raw * 0.999) ?? 10 * magnitude
  return { max: Math.ceil(max / step - 1e-9) * step, stepSize: step }
}

/**
 * Decimals for the ticks of an axis: the same for every tick, enough for the step (0,25 needs two) and at least
 * one below 10 and two below 1 (0,0 € ... 6,0 €; 0,00 € ... 0,75 €; 0 kWh ... 120 kWh).
 */
function axisDecimals(axis: { max: number, stepSize: number }): number {
  const byMax = axis.max < 1 ? 2 : axis.max < 10 ? 1 : 0
  const byStep = [0, 1, 2, 3].find(d => Math.abs(Math.round(axis.stepSize * 10 ** d) - axis.stepSize * 10 ** d) < 1e-9) ?? 3
  return Math.max(byMax, byStep)
}

function tick(slot: number, scale: HeatPumpScale): string {
  const date = new Date(slot)
  switch (scale) {
    case 'minute':
    case 'quarter':
    case 'hour':
      return lang.timeFromStamp(slot)
    case 'year':
      return `${date.getFullYear()}`
    default:
      return lang.timeLabel(slot, scale)
  }
}

// Stripes for slices with incomplete data.
function hatch(color: string): CanvasPattern | string {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 6
  const g = canvas.getContext('2d')
  if (!g) return color
  g.fillStyle = color
  g.globalAlpha = 0.35
  g.fillRect(0, 0, 6, 6)
  g.globalAlpha = 1
  g.strokeStyle = color
  g.lineWidth = 1.5
  g.beginPath()
  g.moveTo(0, 6)
  g.lineTo(6, 0)
  g.stroke()
  return g.createPattern(canvas, 'repeat') || color
}

/**
 * History of a Modbus heat pump (port 0x23): electricity used, heat produced
 * and the cost of the electricity, split into space heating and hot water,
 * with the outdoor temperature (docs/heat-pump-history.md, 4.7).
 *
 * A failure inside the section shows an inline error instead of taking the
 * whole page down.
 */
export function HeatPumpHistory(props: HeatPumpHistoryProps): React.ReactElement {
  const { t } = useTranslation()
  return (
    <HeatPumpHistoryBoundary key={props.port.id} fallback={t('HeatPumpHistory-Failed')}>
      <HeatPumpHistoryView {...props}/>
    </HeatPumpHistoryBoundary>
  )
}

class HeatPumpHistoryBoundary extends Component<{ fallback: string, children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(`HeatPumpHistory: ${error.message}`, `${error.stack ?? ''}\n${info.componentStack ?? ''}`)
  }

  render() {
    if (!this.state.failed) return this.props.children
    return (
      <Box data-cy="heat-pump-history-failed" role="alert" sx={{ backgroundColor: 'panelBackground.main', borderRadius: '0.5rem', p: 2, color: 'grey.400' }}>
        {this.props.fallback}
      </Box>
    )
  }
}

function HeatPumpHistoryView(props: HeatPumpHistoryProps): React.ReactElement {
  const { port, includeFees } = props
  const { t } = useTranslation()
  const app = useApp()
  const theme = useTheme()
  const spaceColor = theme.palette.secondary.main

  const [scale, setScale] = useState<HeatPumpScale>('day')
  // Moment to centre the window on after a scale switch; null = the latest window.
  const [anchor, setAnchor] = useState<DateTime | null>(null)
  // [loadedFrom, loadedTo) of `scale`: what is fetched; it grows to the left while the user pans into the past,
  // and to the right up to the present when the window was opened around a moment in the past.
  const [loaded, setLoaded] = useState<{ scale: HeatPumpScale, from: DateTime, to: DateTime } | null>(null)
  const [rows, setRows] = useState<HeatPumpHistoryRow[]>([])
  // Slice indexes in view (inclusive), in the slots of the loaded range.
  const [view, setView] = useState<{ min: number, max: number } | null>(null)
  const [fetching, setFetching] = useState(true)
  // The slice under the midpoint line: the one the chart headers describe, as in the meter and battery history.
  const [mid, setMid] = useState<number | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  // The tile icon whose explanation is open; pinned when opened by a click or tap.
  const [tip, setTip] = useState<{ id: string, pinned: boolean } | null>(null)
  const tipTimerRef = useRef<number | undefined>(undefined)

  const viewRef = useRef<{ min: number, max: number } | null>(null)
  const loadedRef = useRef(loaded)
  loadedRef.current = loaded
  const loadingLeftRef = useRef(false)
  const loadingRightRef = useRef(false)
  const emptyPagesRef = useRef(0)
  // Identity of the scale/anchor being shown: a page answered after a switch is dropped.
  const generationRef = useRef(0)

  // Only the range fetched for this scale: right after a switch `loaded` still holds the previous scale's range.
  const slots = useMemo(() => (loaded && loaded.scale === scale ? heatPumpSlots(scale, loaded.from, loaded.to) : []), [scale, loaded])
  const placed = useMemo(() => heatPumpOnSlots(rows.map(r => heatPumpWithFees(r, includeFees)), slots), [rows, slots, includeFees])
  // The period the stat boxes add up: as many slices as are in view, ending at the slice under the midpoint line
  // (the latest slice when the line is past the present). At opening that is the latest window up to today.
  const period = useMemo(() => {
    const latest = heatPumpLatestSlice(slots)
    if (!view || latest < 0) return null
    const end = mid !== null ? Math.min(mid, latest) : latest
    return { start: Math.max(0, end - (view.max - view.min)), end }
  }, [slots, view, mid])
  const visible = useMemo(() => {
    if (!period) return []
    return placed.slice(period.start, period.end + 1).filter((r): r is HeatPumpHistoryRow => r !== null)
  }, [placed, period])
  const totals = useMemo(() => heatPumpTotals(visible), [visible])

  const fetchRows = (from: DateTime, to: DateTime) => callAxios<HeatPumpHistoryRow[]>({
    url: `/history/heat_pump/${port.id}?from=${from.toUTC().toISO()}&to=${to.toUTC().toISO()}&steps=${scale}`,
  })

  // Open a scale: the window in view plus one page before it, so there is something to pan into.
  useEffect(() => {
    const generation = ++generationRef.current
    const [from, to] = heatPumpWindowAround(scale, anchor)
    const loadFrom = heatPumpPageBefore(scale, from)
    setFetching(true)
    setRows([])
    setLoaded(null)
    setView(null)
    viewRef.current = null
    emptyPagesRef.current = 0
    loadingLeftRef.current = false
    loadingRightRef.current = false

    // An answer for a previous scale or anchor (out of order, after a switch) is dropped.
    fetchRows(loadFrom, to).then(res => {
      if (generation !== generationRef.current) return
      setFetching(false)
      if (!res.success) {
        app.addError(res)
        return
      }
      const all = heatPumpSlots(scale, loadFrom, to)
      const first = heatPumpSlots(scale, loadFrom, from).length
      // Opened on the present: the latest slice under the midpoint line, the empty slices after it on the right
      // (Ricardo, 29.10.2026: a heat pump with data only from today looked empty when the line sat half a window
      // back). Opened around a moment in the past (a scale switch after panning): that moment under the line.
      const width = all.length - first
      const half = Math.floor(width / 2)
      // The latest slice with a completed minute: the window may end with slices still to come (the rest of the
      // hour on 15 min), and the minute in progress has no data yet.
      const current = Math.max(heatPumpLatestSlice(all), 0)
      const initial = anchor === null
        ? { min: current - half, max: current + (width - 1 - half) }
        : { min: first, max: all.length - 1 }
      const fetched = { scale, from: loadFrom, to }
      viewRef.current = initial
      loadedRef.current = fetched
      setView(initial)
      setRows(heatPumpRows(res.data))
      setLoaded(fetched)
      // Opened around a moment in the past: fetch on towards the present right away.
      loadMoreRightRef.current()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port.id, scale, anchor])

  // Panned near the left edge: fetch the page before and keep the same slices in view.
  const loadMoreLeft = () => {
    const current = loadedRef.current
    if (!current || current.scale !== scale || loadingLeftRef.current || emptyPagesRef.current >= MAX_EMPTY_PAGES) return
    const generation = generationRef.current
    const oldFrom = current.from
    const newFrom = heatPumpPageBefore(scale, oldFrom)
    loadingLeftRef.current = true

    fetchRows(newFrom, oldFrom).then(res => {
      if (generation !== generationRef.current) return
      loadingLeftRef.current = false
      if (!res.success) return
      const added = heatPumpRows(res.data)
      emptyPagesRef.current = added.length === 0 ? emptyPagesRef.current + 1 : 0
      const shift = heatPumpSlots(scale, newFrom, oldFrom).length
      const inView = viewRef.current
      if (inView) {
        const shifted = { min: inView.min + shift, max: inView.max + shift }
        viewRef.current = shifted
        setView(shifted)
      }
      setRows(prev => [...added, ...prev])
      setLoaded(prev => (prev ? { ...prev, from: newFrom } : prev))
    })
  }
  const loadMoreLeftRef = useRef(loadMoreLeft)
  loadMoreLeftRef.current = loadMoreLeft

  // Panned near the right edge of a range that ends before the present: fetch the page after it.
  const loadMoreRight = () => {
    const current = loadedRef.current
    if (!current || current.scale !== scale || loadingRightRef.current) return
    const oldTo = current.to
    const newTo = heatPumpPageAfter(scale, oldTo)
    if (newTo === null) return
    const generation = generationRef.current
    loadingRightRef.current = true

    fetchRows(oldTo, newTo).then(res => {
      if (generation !== generationRef.current) return
      loadingRightRef.current = false
      if (!res.success) return
      const added = heatPumpRows(res.data)
      setRows(prev => [...prev, ...added])
      setLoaded(prev => (prev ? { ...prev, to: newTo } : prev))
    })
  }
  const loadMoreRightRef = useRef(loadMoreRight)
  loadMoreRightRef.current = loadMoreRight

  const energyRef = useRef<HTMLCanvasElement>(null)
  const tempRef = useRef<HTMLCanvasElement>(null)
  const costRef = useRef<HTMLCanvasElement>(null)
  const chartsRef = useRef<Chart[]>([])
  const midRef = useRef<number | null>(null)

  useEffect(() => {
    if (!energyRef.current || !tempRef.current || !costRef.current) return

    // Empty slices after the present, half a window: panning can bring the latest slice under the midpoint line,
    // as in the meter and battery history.
    const inView = viewRef.current
    const pad = inView ? Math.floor((inView.max - inView.min + 1) / 2) : 0
    const padded: (HeatPumpHistoryRow | null)[] = [...placed, ...new Array(pad).fill(null)]
    const groups = slots.map(s => ({ from: s.toMillis() })) as unknown as SeriesGroup[]
    const plain = [...slots.map(s => tick(s.toMillis(), scale)), ...new Array(pad).fill('')]
    const labels = scale === 'quarter' ? quarterHourLabels(plain, groups) : plain
    const patterns = { space: hatch(spaceColor), dhw: hatch(DHW_COLOR) }
    const incomplete = (i: number) => (padded[i]?.coverage ?? 1) < HEAT_PUMP_INCOMPLETE
    const split = padded.map(r => (r ? heatPumpSplit(r) : null))
    const value = (i: number, key: 'elecSpace' | 'elecDhw' | 'heatSpace' | 'heatDhw' | 'costSpace' | 'costDhw') => {
      const s = split[i]
      if (!s) return null
      return key.startsWith('cost') ? s[key] : heatPumpChartValue(s[key], scale)
    }

    // The slice under the midpoint line, for the headers (clamped to the latest completed one when rendered).
    const follow = (min: number, max: number) => {
      const i = Math.round((min + max) / 2)
      const next = i >= 0 ? i : null
      if (next !== midRef.current) {
        midRef.current = next
        setMid(next)
      }
    }

    const stackTop = (s: ReturnType<typeof heatPumpSplit> | null) =>
      s === null ? 0 : heatPumpChartValue(Math.max(s.heatSpace + s.heatDhw, s.elecSpace + s.elecDhw), scale)
    const energyAxis = heatPumpAxis(Math.max(0, ...split.map(stackTop)))
    const costAxis = heatPumpAxis(Math.max(0, ...split.map(s => (s ? s.costSpace + s.costDhw : 0))))
    const yAxis = (axis: { max: number, stepSize: number } | null, format: (v: number) => string, extra: object = {}) => ({
      afterFit: (a: { width: number }) => { a.width = AXIS_WIDTH },
      grid: GRID,
      border: { display: false },
      ...(axis ? { min: 0, max: axis.max } : {}),
      ticks: {
        color: theme.palette.grey[400],
        padding: 6,
        ...(axis ? { stepSize: axis.stepSize } : { maxTicksLimit: 3, precision: 0 }),
        callback: (v: string | number) => format(Number(v)),
      },
      ...extra,
    })
    // The view survives the charts being rebuilt (new page loaded, data refreshed).
    const initial = viewRef.current ?? { min: 0, max: Math.max(slots.length - 1, 0) }
    // Panning stops with the latest completed slice under the midpoint line: never past the present.
    const width = initial.max - initial.min + 1
    const panMax = Math.max(heatPumpLatestSlice(slots), 0) + (width - 1 - Math.floor(width / 2))
    follow(initial.min, initial.max)
    const xAxis = {
      offset: true,
      min: initial.min,
      max: initial.max,
      grid: { display: false },
      border: { display: false },
      // No room reserved for the first and last tick label: the plot areas of the three charts, and so their
      // midpoint lines, stay exactly above each other.
      afterFit: (axis: { paddingLeft: number, paddingRight: number }) => { axis.paddingLeft = 0; axis.paddingRight = 0 },
      ticks: { color: theme.palette.grey[400], font: { size: 10 }, maxRotation: 0, autoSkipPadding: 12, autoSkip: scale === 'quarter' ? false : undefined },
    }
    // Drag or swipe pans all three charts together, as on the meter and battery history.
    const syncFrom = (chart: Chart) => {
      const min = chart.scales.x.min
      const max = chart.scales.x.max
      chartsRef.current.filter(c => c !== chart).forEach(c => {
        const x = c.options.scales?.x
        if (x) {
          x.min = min
          x.max = max
          c.update('none')
        }
      })
      follow(min, max)
      return { min: Math.round(min), max: Math.round(max) }
    }
    const plugins = [
      midpointLinePlugin(theme.palette.chartMidpointLine.main),
      ...(scale === 'quarter' ? [quarterHourBandsPlugin(groups)] : []),
    ]
    const common = {
      animation: false as const,
      maintainAspectRatio: false,
      responsive: true,
      // The same room after the plot area in every chart, for the last tick label.
      layout: { padding: { right: 18 } },
      events: [],
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        zoom: {
          // Pannable over everything loaded up to the present; older pages are loaded near the left edge.
          limits: { x: { min: 0, max: Math.min(Math.max(padded.length - 1, 0), panMax) } },
          pan: {
            enabled: true,
            mode: 'x' as const,
            onPan: ({ chart }: { chart: Chart }) => { syncFrom(chart) },
            onPanComplete: ({ chart }: { chart: Chart }) => {
              const next = syncFrom(chart)
              viewRef.current = next
              setView(next)
              if (next.min < LOAD_MORE_THRESHOLD) loadMoreLeftRef.current()
              if (next.max > slots.length - 1 - LOAD_MORE_THRESHOLD) loadMoreRightRef.current()
            },
          },
        },
      },
    }
    // Stacked segments meet without a gap or a line (Joni, 29.10.2026); the top one has a rounded end.
    const segment = (color: string, pattern: CanvasPattern | string, isTop: (i: number) => boolean) => ({
      backgroundColor: (ctx: { dataIndex: number }) => (incomplete(ctx.dataIndex) ? pattern : color),
      borderRadius: (ctx: { dataIndex: number }) => (isTop(ctx.dataIndex) ? { topLeft: 4, topRight: 4 } : 0),
      borderSkipped: 'start' as const,
      borderWidth: 0,
    })
    // The heat envelope is stacked the same way, without hatching.
    const heatSegment = (color: string, isTop: (i: number) => boolean) => ({
      backgroundColor: color,
      borderRadius: (ctx: { dataIndex: number }) => (isTop(ctx.dataIndex) ? { topLeft: 4, topRight: 4 } : 0),
      borderSkipped: 'start' as const,
      borderWidth: 0,
    })
    const temp = padded.map(r => r?.t_outdoor ?? null)
    // A slice with data but nothing to stack (an idle heat pump) gets a mark on the baseline in the dataset's colour,
    // so that it reads as zero rather than as missing data (Joni, 29.10.2026).
    const zeroMarks = (id: string, datasetIndex: number, color: string, isZero: (i: number) => boolean) => ({
      id,
      afterDatasetsDraw(chart: Chart) {
        const y = chart.scales.y.getPixelForValue(0)
        const { left, right, top, bottom } = chart.chartArea
        const g = chart.ctx
        g.save()
        g.beginPath()
        g.rect(left, top, right - left, bottom - top + 1)
        g.clip()
        g.fillStyle = color
        chart.getDatasetMeta(datasetIndex).data.forEach((bar, i) => {
          if (!isZero(i)) return
          const { x, width } = bar.getProps(['x', 'width'], true) as { x: number, width: number }
          if (Number.isFinite(x) && Number.isFinite(width)) g.fillRect(x - width / 2, y - 2, width, 2)
        })
        g.restore()
      }
    })
    const costZero = (i: number) => {
      const s = split[i]
      return s !== null && s.costSpace + s.costDhw < 0.00005
    }
    const heatTop = (i: number) => !((split[i]?.heatDhw ?? 0) > 0.005)
    const elecTop = (i: number) => !((split[i]?.elecDhw ?? 0) > 0.005)
    const costTop = (i: number) => !((split[i]?.costDhw ?? 0) > 0.0005)
    const unit = heatPumpShowsPower(scale) ? 'kW' : 'kWh'
    const indexes = padded.map((_r, i) => i)
    const heatBar = { stack: 'heat', grouped: false, order: 3, barPercentage: 0.92, categoryPercentage: 0.9, maxBarThickness: 24 }
    const elecBar = { stack: 'elec', grouped: false, order: 1, barPercentage: 0.46, categoryPercentage: 0.9, maxBarThickness: 12 }
    const costBar = { stack: 'cost', barPercentage: 0.8, categoryPercentage: 0.9, maxBarThickness: 24 }

    const energy: ChartConfiguration<'bar'> = {
      type: 'bar',
      plugins: [...plugins, zeroMarks('heatPumpZeroEnergy', 2, spaceColor, i => heatPumpNoElectricity(padded[i]))],
      data: {
        labels,
        datasets: [
          { label: t('HeatPumpHistory-Heat, space heating'), ...heatBar, data: indexes.map(i => value(i, 'heatSpace')), ...heatSegment(HEAT_SPACE_COLOR, heatTop) },
          { label: t('HeatPumpHistory-Heat, hot water'), ...heatBar, data: indexes.map(i => value(i, 'heatDhw')), ...heatSegment(HEAT_DHW_COLOR, () => true) },
          { label: t('HeatPumpHistory-Electricity, space heating'), ...elecBar, data: indexes.map(i => value(i, 'elecSpace')), ...segment(spaceColor, patterns.space, elecTop) },
          { label: t('HeatPumpHistory-Electricity, hot water'), ...elecBar, data: indexes.map(i => value(i, 'elecDhw')), ...segment(DHW_COLOR, patterns.dhw, () => true) },
        ],
      },
      options: {
        ...common,
        scales: { x: xAxis, y: { ...yAxis(energyAxis, v => `${num(v, axisDecimals(energyAxis))} ${unit}`), stacked: true } },
      },
    } as ChartConfiguration<'bar'>

    const temperature: ChartConfiguration<'line'> = {
      type: 'line',
      plugins,
      data: {
        labels,
        datasets: [{
          data: temp,
          borderColor: theme.palette.grey[700],
          borderWidth: 2,
          // A line needs two points: a value without a neighbour (the only slice of data, or one between gaps) is a dot.
          pointRadius: (ctx: { dataIndex: number }) => (heatPumpIsolated(temp, ctx.dataIndex) ? 3 : 0),
          pointBackgroundColor: theme.palette.grey[700],
          tension: 0.3,
          spanGaps: false,
        }],
      },
      options: {
        ...common,
        scales: {
          x: { ...xAxis, ticks: { display: false } },
          y: yAxis(null, v => `${num(v, 0)} °C`),
        },
      },
    } as ChartConfiguration<'line'>

    const cost: ChartConfiguration<'bar'> = {
      type: 'bar',
      plugins: [...plugins, zeroMarks('heatPumpZeroCost', 0, spaceColor, costZero)],
      data: {
        labels,
        datasets: [
          { label: t('HeatPumpHistory-Space heating'), ...costBar, data: indexes.map(i => value(i, 'costSpace')), ...segment(spaceColor, patterns.space, costTop) },
          { label: t('HeatPumpHistory-Hot water'), ...costBar, data: indexes.map(i => value(i, 'costDhw')), ...segment(DHW_COLOR, patterns.dhw, () => true) },
        ],
      },
      options: {
        ...common,
        scales: { x: xAxis, y: { ...yAxis(costAxis, v => `${num(v, axisDecimals(costAxis))} €`), stacked: true } },
      },
    } as ChartConfiguration<'bar'>

    const charts = [
      new Chart(energyRef.current, energy),
      new Chart(tempRef.current, temperature),
      new Chart(costRef.current, cost),
    ]
    chartsRef.current = charts as Chart[]

    return () => {
      charts.forEach(c => c.destroy())
      chartsRef.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed, scale, t, spaceColor])

  // The slice the headers describe: the one under the midpoint line, but never one after the latest completed
  // slice (the minute in progress reads zero).
  const latest = heatPumpLatestSlice(slots)
  const shown = mid === null || latest < 0 ? null : Math.min(mid, latest)
  const shownRow = shown === null ? null : placed[shown] ?? null
  const shownSplit = shownRow ? heatPumpSplit(shownRow) : null
  const shownElectricityPrice = shownRow ? heatPumpElectricityPrice(shownRow) : null
  const shownHeatPrice = shownRow ? heatPumpHeatPrice(shownRow) : null
  const shownTime = shown === null ? '' : lang.timeLabel(slots[shown].toMillis(), scale)
  const unit = heatPumpShowsPower(scale) ? 'kW' : 'kWh'
  const chartNum = (kwhValue: number) => num(heatPumpChartValue(kwhValue, scale), 1)
  const noData = !fetching && rows.length === 0

  // An icon with an explanation. Hover shows the explanation; a click or tap pins it open (a quick tap does not
  // open MUI's touch tooltip, and the mouse events a tap emulates would close it again) until a tap elsewhere or a
  // few seconds pass.
  const explained = (id: string, name: string, help: string, icon: React.ReactElement, sx: object = {}) => {
    const close = () => setTip(current => (current?.id === id ? null : current))
    return (
      <ClickAwayListener key={id} onClickAway={close}>
        <Tooltip
          title={<>{name && <><b>{name}</b><br/></>}{help}</>}
          arrow
          // The theme's grey 700 (MUI's tooltip colour) is light: white text on it does not read.
          componentsProps={{
            tooltip: { sx: { backgroundColor: 'grey.800', color: 'grey.100', fontSize: '0.8rem', lineHeight: 1.45, p: 1.25, boxShadow: 3 } },
            arrow: { sx: { color: 'grey.800' } },
          }}
          open={tip?.id === id}
          onOpen={() => setTip(current => (current?.pinned ? current : { id, pinned: false }))}
          onClose={() => setTip(current => (current?.id === id && !current.pinned ? null : current))}
          disableTouchListener
        >
          <Box
            component="span"
            tabIndex={0}
            onClick={() => {
              setTip({ id, pinned: true })
              window.clearTimeout(tipTimerRef.current)
              tipTimerRef.current = window.setTimeout(close, 4000)
            }}
            aria-label={name ? `${name}: ${help}` : help}
            sx={{ display: 'inline-flex', color: 'grey.300', cursor: 'help', ...sx }}
          >
            {icon}
          </Box>
        </Tooltip>
      </ClickAwayListener>
    )
  }
  // Enion icons for the two purposes, the icon's accent in the series colour.
  const purposeIcon = (id: string, purpose: 'space' | 'dhw', color: string) => {
    const name = purpose === 'space' ? t('HeatPumpHistory-Space heating') : t('HeatPumpHistory-Hot water')
    const help = purpose === 'space' ? t('HeatPumpHistory-Space heating help') : t('HeatPumpHistory-Hot water help')
    const Icon = purpose === 'space' ? RadiatorIcon : BoilerIcon
    return explained(id, name, help, <Icon sx={{ fontSize: '1.5rem' }}/>, { '--enion-icon-accent': color, '--enion-icon-cut': 'transparent' })
  }

  // The stat boxes of the meter and solar Tilastot: the figure first, the label under it; here with the split
  // into space heating and hot water under the label.
  const tile = (label: string, value: string, lines: [string, string][]) => (
    <Box className="hp-tile" sx={{ backgroundColor: 'statsHistoryBackground.main', borderRadius: '0.375rem', p: 2, minWidth: 0, lineHeight: '1.1rem' }}>
      <Box className="hp-tile-value" sx={{ color: 'chartText.main', fontWeight: 400, fontSize: '2rem', lineHeight: '2.2rem', whiteSpace: 'nowrap' }}>{value}</Box>
      <Box>{label}</Box>
      <Box sx={{ mt: 1 }}>
        {lines.map(([color, text], i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            {purposeIcon(`${label}-${i}`, i === 0 ? 'space' : 'dhw', color)}
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{text}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  )

  // Legend under a chart, as under the price history: a short line in the series colour and its name.
  const legend = (items: [string, string, number?][]) => (
    <Box sx={{ mt: 1, px: 1, display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
      {items.map(([color, label, height]) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 24, height: height ?? 4, borderRadius: '1px', backgroundColor: color, flexShrink: 0 }}/>
          <Typography variant="body2" sx={{ color: 'grey.400', fontSize: '0.7rem' }}>{label}</Typography>
        </Box>
      ))}
    </Box>
  )

  // A figure of the chart header with its series name under it, as the battery history header.
  const figure = (value: string, figureUnit: string, label: string, color: string) => (
    <Box>
      <ChartSummary sx={{ m: 0 }} main={value} unit={figureUnit}/>
      <Typography sx={{ color, fontWeight: 600 }}>{label}</Typography>
    </Box>
  )
  const time = <Typography data-cy="heat-pump-history-time" sx={{ color: 'chartText.main', fontSize: '0.75rem', fontWeight: 400, whiteSpace: 'nowrap' }}>{shownTime}</Typography>
  const details = (dataCy: string, content: ReactNode) => (
    <Box data-cy={dataCy} sx={{ ml: 4, mb: 2, minHeight: '1.3rem', fontSize: '0.8rem', display: 'flex', flexWrap: 'wrap', columnGap: 2, '& b': { color: 'chartText.main', fontWeight: 400 } }}>
      {noData ? <span>{t('HeatPumpHistory-No data')}</span> : shown !== null && !shownRow ? <span>{t('HeatPumpHistory-No data in this slice')}</span> : content}
    </Box>
  )

  // The period in view, from the first to the last visible slice: what the stat boxes add up.
  const periodScale = scale === 'month' || scale === 'year' ? 'month' : 'day'
  const periodLabel = period
    ? `${lang.timeLabel(slots[period.start].toMillis(), periodScale)} – ${lang.timeLabel(slots[period.end].toMillis(), periodScale)}`
    : ''

  // Switching scale keeps the moment in the middle of the view in view.
  // Switching scale opens the present while the latest slice is in view; after panning it away into the past, the
  // new scale opens on the moment under the midpoint line.
  const switchScale = (next: HeatPumpScale) => {
    if (next === scale) return
    setAnchor(heatPumpSwitchAnchor(scale, slots, view))
    setScale(next)
  }

  const helpItems: [string, string][] = [
    [t('HeatPumpHistory-Electricity used'), t('HeatPumpHistory-Electricity used help')],
    [t('HeatPumpHistory-Heat produced'), t('HeatPumpHistory-Heat produced help')],
    [t('HeatPumpHistory-Efficiency (COP)'), t('HeatPumpHistory-COP help')],
    [t('HeatPumpHistory-Heating cost'), includeFees ? `${t('HeatPumpHistory-Cost note')} ${t('Includes the margin and transfer fee from your settings.')}` : t('HeatPumpHistory-Cost note')],
    [t('HeatPumpHistory-Electricity price'), t('HeatPumpHistory-Electricity price help')],
    [t('HeatPumpHistory-Heat price'), t('HeatPumpHistory-Heat price help')],
    [t('HeatPumpHistory-Space heating'), t('HeatPumpHistory-Space heating help')],
    [t('HeatPumpHistory-Hot water'), t('HeatPumpHistory-Hot water help')],
  ]

  return (
    <Box data-cy="heat-pump-history">
      <Box sx={{ position: 'relative', mb: 2, display: 'flex', justifyContent: 'center' }}>
        <RadioSelector
          variant="secondary"
          value={scale}
          disabled={fetching}
          onClick={value => switchScale(value as HeatPumpScale)}
          values={{
            minute: t('Scale-Minute'),
            quarter: t('Scale-Quarter'),
            hour: t('Scale-Hour'),
            day: t('Scale-Day'),
            week: t('Scale-Week'),
            month: t('Scale-Month'),
            year: t('Scale-Year'),
          }}
        />
        <IconButton size="small" aria-label={t('Help')} onClick={() => setHelpOpen(true)} sx={{ position: 'absolute', right: 0, top: 0 }}>
          <HelpOutline fontSize="small"/>
        </IconButton>
      </Box>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Help')}</DialogTitle>
        <DialogContent dividers>
          {helpItems.map(([term, text]) => (
            <Box key={term} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>{term}</Typography>
              <Typography variant="body2" color="text.secondary">{text}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      <Box data-cy="heat-pump-history-period" sx={{ textAlign: 'center', mb: 2, minHeight: '1.3rem', fontSize: '0.875rem', color: 'text.secondary' }}>{periodLabel}</Box>

      {/* Stat boxes follow the width of this column, not of the window: with the side menu open the column is much
          narrower than the window. Container queries: 2 x 2 by default, 4 in a row when there is room, a smaller
          figure on a phone. */}
      <Box sx={{ containerType: 'inline-size', mb: 4 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '0.5rem',
          fontSize: '0.8rem',
          '@container (min-width: 52rem)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
          '@container (max-width: 24rem)': { '& .hp-tile-value': { fontSize: '1.6rem' } },
        }}>
          {tile(t('HeatPumpHistory-Electricity used'), kwh(totals.elec), [[spaceColor, kwh(totals.elecSpace)], [DHW_COLOR, kwh(totals.elecDhw)]])}
          {tile(t('HeatPumpHistory-Heat produced'), kwh(totals.heat), [[spaceColor, kwh(totals.heatSpace)], [DHW_COLOR, kwh(totals.heatDhw)]])}
          {tile(t('HeatPumpHistory-Efficiency (COP)'), cop(totals.cop), [[spaceColor, cop(totals.copSpace)], [DHW_COLOR, cop(totals.copDhw)]])}
          {tile(t('HeatPumpHistory-Heating cost'), eur(totals.cost), [[spaceColor, eur(totals.costSpace)], [DHW_COLOR, eur(totals.costDhw)]])}
        </Box>
      </Box>

      <PageTitle sx={{ mb: 2 }} title={heatPumpShowsPower(scale) ? t('HeatPumpHistory-Power') : t('HeatPumpHistory-Energy')}/>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', columnGap: 2, mb: 1, ml: 4 }}>
        <Box data-cy="heat-pump-history-summary" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
          {figure(shownSplit ? chartNum(shownSplit.elecSpace + shownSplit.elecDhw) : '—', unit, t('HeatPumpHistory-Electricity'), spaceColor)}
          {figure(shownSplit ? chartNum(shownSplit.heatSpace + shownSplit.heatDhw) : '—', unit, t('HeatPumpHistory-Heat'), HEAT_LABEL_COLOR)}
        </Box>
        {time}
      </Box>
      {details('heat-pump-history-details', shownRow && <>
        <span>COP <b>{cop(shownRow.cop)}</b></span>
        {shownRow.t_outdoor !== null && <span>{t('HeatPumpHistory-Outdoors')} <b>{num(shownRow.t_outdoor, 1)} °C</b></span>}
        {shownSplit && shownSplit.aux > 0.01 && <span>{t('HeatPumpHistory-Backup heater')} <b>{kwh(shownSplit.aux)}</b></span>}
        {shownRow.coverage < HEAT_PUMP_INCOMPLETE &&
          <Box component="span" sx={{ color: 'warning.light' }}>{t('HeatPumpHistory-Data missing')} {num((1 - shownRow.coverage) * 100, 0)} %</Box>}
      </>)}
      <Box sx={{ position: 'relative', height: { xs: 220, sm: 260 } }}>
        <canvas ref={energyRef} aria-label={t('HeatPumpHistory-Energy')} />
      </Box>
      <Box sx={{ position: 'relative', height: 90, mt: 1 }}>
        <canvas ref={tempRef} aria-label={t('HeatPumpHistory-Outdoor temperature')} />
      </Box>
      {legend([
        [spaceColor, t('HeatPumpHistory-Electricity, space heating')], [DHW_COLOR, t('HeatPumpHistory-Electricity, hot water')],
        [HEAT_SPACE_COLOR, t('HeatPumpHistory-Heat, space heating')], [HEAT_DHW_COLOR, t('HeatPumpHistory-Heat, hot water')],
        [theme.palette.grey[700], t('HeatPumpHistory-Outdoor temperature'), 2],
      ])}

      <PageTitle sx={{ mt: 4, mb: 2 }} title={t('HeatPumpHistory-Heating cost')}/>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', columnGap: 2, mb: 1, ml: 4 }}>
        <Box data-cy="heat-pump-history-cost-summary" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
          {figure(shownSplit ? num(shownSplit.costSpace, 2) : '—', '€', t('HeatPumpHistory-Space heating'), spaceColor)}
          {figure(shownSplit ? num(shownSplit.costDhw, 2) : '—', '€', t('HeatPumpHistory-Hot water'), DHW_COLOR)}
        </Box>
        {time}
      </Box>
      {details('heat-pump-history-cost-details', shownRow && shownSplit && <>
        <span>{t('HeatPumpHistory-Total')} <b>{eur(shownSplit.costSpace + shownSplit.costDhw)}</b></span>
        {shownElectricityPrice !== null && <span>{t('HeatPumpHistory-Electricity price')} <b>{num(shownElectricityPrice, 1)} c/kWh</b></span>}
        {shownHeatPrice !== null && <span>{t('HeatPumpHistory-Heat price')} <b>{num(shownHeatPrice, 1)} c/kWh</b></span>}
      </>)}
      <Box sx={{ position: 'relative', height: { xs: 220, sm: 260 } }}>
        <canvas ref={costRef} aria-label={t('HeatPumpHistory-Heating cost')} />
      </Box>
      {legend([[spaceColor, t('HeatPumpHistory-Space heating')], [DHW_COLOR, t('HeatPumpHistory-Hot water')]])}
    </Box>
  )
}
